"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { login, verifyOTP, passkeyLogin, isPasskeySupported, setToken } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, ArrowLeft, Fingerprint, Mail, Activity } from "lucide-react";

export function LoginForm() {
    const [email, setEmail] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [step, setStep] = useState<"email" | "verify">("email");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [passkeySupported, setPasskeySupported] = useState(false);

    useEffect(() => {
        setPasskeySupported(isPasskeySupported());
    }, []);

    const handlePasskeyLogin = async () => {
        setError("");
        setLoading(true);
        try {
            const { token } = await passkeyLogin();
            setToken(token);
            window.location.href = "/app";
        } catch {
            setError("Passkey login failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await login(email);
            setStep("verify");
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const { token } = await verifyOTP(email, otpCode);
            setToken(token);
            window.location.href = "/app";
        } catch {
            setError("Invalid or expired code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (step === "verify") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background p-4 bg-grid">
                <div className="w-full max-w-sm">
                    <div className="glass-card rounded-xl p-6">
                        <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/15">
                            <Mail className="h-5 w-5 text-primary" />
                        </div>

                        <h1 className="text-center text-xl font-semibold tracking-tight">
                            Check your inbox
                        </h1>
                        <p className="mt-1.5 text-center text-sm text-muted-foreground">
                            We sent a sign-in code to{" "}
                            <span className="font-data text-xs text-foreground">{email}</span>
                        </p>

                        <form onSubmit={handleVerifySubmit} className="mt-6 space-y-4">
                            {error && (
                                <div className="flex items-center gap-2.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
                                    <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                                    <p className="text-xs text-destructive">{error}</p>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="otp" className="text-xs text-muted-foreground">
                                    6-digit code
                                </Label>
                                <Input
                                    id="otp"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                                    required
                                    autoFocus
                                    className="font-data text-center text-2xl tracking-[0.5em]"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full glow-green"
                                disabled={loading || otpCode.length !== 6}
                            >
                                {loading ? "Verifying..." : "Verify"}
                            </Button>
                        </form>

                        <div className="mt-5 flex justify-center">
                            <button
                                onClick={() => { setStep("email"); setError(""); setOtpCode(""); }}
                                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowLeft className="h-3 w-3" />
                                Use a different email
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4 bg-grid">
            <div className="w-full max-w-sm">
                <div className="glass-card rounded-xl p-6">
                    <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 pulse-healthy">
                        <Activity className="h-5 w-5 text-primary" />
                    </div>

                    <h1 className="text-center text-xl font-semibold tracking-tight">
                        Sign in to PingNG
                    </h1>
                    <p className="mt-1.5 text-center text-sm text-muted-foreground">
                        Enter your email to receive a sign-in code
                    </p>

                    <form onSubmit={handleEmailSubmit} className="mt-6 space-y-4">
                        {error && (
                            <div className="flex items-center gap-2.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
                                <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                                <p className="text-xs text-destructive">{error}</p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs text-muted-foreground">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full glow-green"
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Send Magic Link"}
                        </Button>
                    </form>

                    {passkeySupported && (
                        <>
                            <div className="relative my-5">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border/50" />
                                </div>
                                <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                                    <span className="bg-card px-2 text-muted-foreground">or</span>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full gap-2"
                                onClick={handlePasskeyLogin}
                                disabled={loading}
                            >
                                <Fingerprint className="h-4 w-4" />
                                {loading ? "Authenticating..." : "Sign in with passkey"}
                            </Button>
                        </>
                    )}

                    <div className="mt-5 flex justify-center">
                        <p className="text-xs text-muted-foreground">
                            Don&apos;t have an account?{" "}
                            <Link href="/register" className="text-primary hover:underline underline-offset-4">
                                Create one
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
